import {
  CARRIER_MANAGEMENT,
  CATEGORY_MANAGEMENT,
  FILTER_FEATURE,
  ROAMING_PARTNER,
  SUPPLIER_MANAGEMENT,
} from "api/ApiPath";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { PackageManagementInjection } from "features/packageManagement/di/PackageManagementInjection";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { MobileDataService } from "utils/enums/MobileDataEnums";
import {
  PackageActionCommandEntity,
  PackageActionRequestEntity,
  PackageCreateRequestEntity,
} from "../entities/PackageActionEntity";
import {
  PackageCategoryEntity,
  PackageLookupEntity,
  PackageLookupRequestEntity,
  PackageSupplierEntity,
} from "../entities/PackageLookupEntity";
import { PackageListRequestEntity } from "../entities/PackageListRequestEntity";
import {
  PackageDetailEntity,
  PackageMoreEntity,
  PackageResponseEntity,
} from "../entities/PackageResponseEntity";
import { IPackageManagementRepository } from "../repositories/IPackageManagementRepository";

const carrierIds: Record<string, string> = {
  reddi: "465ff323-f627-4dac-8646-6c6798c31cb7",
  vnmobile: "5bf938d7-db4a-4bb1-8cc7-0ff7630936dc",
  vinaphone: "d1a4a6c7-b923-4480-b6f2-222a44c52d23",
  mobifone: "7ff6f64c-b8a4-48f2-93c7-b67d74bd30a8",
  viettel: "92a5f0a4-a69f-4c4f-a14b-12e32f86fafd",
};

type PackageActionPlan = {
  request: PackageActionRequestEntity;
  suppliersToAdd: PackageLookupEntity[];
  categoriesToAdd: PackageCategoryEntity[];
};

@injectable()
export class PackageManagementUseCase {
  private packageRepo: IPackageManagementRepository;
  private filterUseCase = FilterInjection.getUseCase();

  constructor() {
    this.packageRepo = PackageManagementInjection.getRepo();
  }

  private changedValue<T>(currentValue: T, nextValue: T): T | undefined {
    return currentValue === nextValue ? undefined : nextValue;
  }

  private buildCreateRequest(
    service: MobileDataService,
    item: PackageActionRequestEntity,
  ): PackageCreateRequestEntity {
    const request = new PackageCreateRequestEntity({ service });

    if (service === MobileDataService.SimEcommerce) {
      request.ecommerceSimMobileData = item;
    } else if (service === MobileDataService.TravelSim) {
      request.travelSimMobileData = item;
    } else if (service === MobileDataService.BuyPackage) {
      request.mobileData = item;
    }

    return request;
  }

  private normalizeMore(more: PackageMoreEntity[] = []): PackageMoreEntity[] {
    return more.map((element) => {
      if (element.type === "section") {
        const { type, ...section } = element;
        return section;
      }

      const { name, type, items, ...tag } = element;
      return tag;
    });
  }

  private buildActionPlan(
    command: PackageActionCommandEntity,
  ): PackageActionPlan {
    const {
      action,
      body = {},
      categoryList = [],
      chosenSupplier = [],
      detailInfo = {},
      service,
      typeKey,
    } = command;
    const bodyRecord = body as Record<string, unknown>;
    const detailRecord = detailInfo as Record<string, unknown>;
    const changedFields = Object.keys(bodyRecord)
      .filter(
        (key) =>
          !["storage", "durationDay", "capacityUnit", "durationUnit"].includes(
            key,
          ) &&
          this.changedValue(detailRecord[key], bodyRecord[key]) !== undefined,
      )
      .reduce<Record<string, unknown>>(
        (result, key) => ({ ...result, [key]: bodyRecord[key] }),
        {},
      );
    const detailSuppliers = detailInfo.suppliers ?? [];
    const newSuppliers = chosenSupplier.filter(
      (supplier) =>
        !detailSuppliers.some((item) => item.code === supplier.code) &&
        !supplier.id,
    );
    const updatedSuppliers = chosenSupplier.filter((supplier) =>
      detailSuppliers.some((item) => {
        const savedSupplier = item.supplier as PackageLookupEntity | undefined;
        return (
          item.id === supplier.id &&
          (item.code !== supplier.code ||
            savedSupplier?.order !== supplier.order)
        );
      }),
    );
    const categoryIds = Array.isArray(body.categories)
      ? body.categories
          .map((category) =>
            typeof category === "string" ? category : category.value,
          )
          .filter((category): category is string => Boolean(category))
      : [];
    const newCategories = categoryIds
      .filter(
        (categoryId) =>
          !detailInfo.categories?.some((item) => {
            const category = item.category;
            return typeof category === "object"
              ? category.id === categoryId
              : category === categoryId;
          }),
      )
      .map((categoryId) =>
        categoryList.find(
          (category) =>
            category.id === categoryId || category.code === categoryId,
        ),
      )
      .filter(
        (category): category is PackageCategoryEntity => Boolean(category),
      );
    const suppliersToAdd = newSuppliers.map((supplier) => ({
      code: supplier.code,
      ...((supplier.supplier as PackageLookupEntity | undefined) ?? {}),
    }));

    const normalizedStorage =
      body.capacityUnit === "mb" && body.storage !== undefined
        ? Math.round((body.storage / 1000) * 200) / 200
        : body.storage;
    const normalizedDuration =
      body.durationDay === undefined
        ? undefined
        : body.durationUnit === "hour" && body.durationDay >= 1
          ? Math.round((body.durationDay / 24) * 200) / 200
          : body.durationUnit === "day" && body.durationDay < 1
            ? Math.round((body.durationDay / 24) * 200) / 200
            : body.durationDay;

    return {
      request: {
        ...changedFields,
        id: detailInfo.id,
        simType: service !== MobileDataService.BuyPackage ? 0 : 1,
        externalCallTotalHour: 0,
        internalCallTotalHour: 0,
        countries: typeKey === "Roaming" ? body.countries : "",
        more: this.normalizeMore(body.more),
        suppliers:
          action === "update"
            ? updatedSuppliers.length > 0
              ? updatedSuppliers
              : undefined
            : suppliersToAdd.length > 0
              ? suppliersToAdd
              : undefined,
        storage: this.changedValue(detailInfo.storage, normalizedStorage),
        durationDay: this.changedValue(
          detailInfo.durationDay,
          normalizedDuration,
        ),
        categories: action === "update" ? undefined : body.categories,
      },
      suppliersToAdd,
      categoriesToAdd: newCategories,
    };
  }

  async getPackageManagementList(
    params: PackageListRequestEntity,
  ): Promise<ListBaseApiResponseModel<PackageResponseEntity>> {
    return this.packageRepo.getPackageList(params);
  }

  async getPackageManagementDetail(
    id: string,
  ): Promise<BaseApiResponseModel<PackageDetailEntity>> {
    return this.packageRepo.getPackageDetail(id);
  }

  async savePackageManagement(
    command: PackageActionCommandEntity,
  ): Promise<BaseApiResponseModel<PackageResponseEntity>> {
    const { request, suppliersToAdd, categoriesToAdd } =
      this.buildActionPlan(command);

    if (command.action === "update") {
      const response = await this.packageRepo.updatePackage(request);
      const packageId = command.detailInfo?.id;

      if (!response?.data || !packageId) return response;

      const additionRequests: Promise<unknown>[] = [];

      if (suppliersToAdd.length > 0) {
        additionRequests.push(
          this.packageRepo.addPackageSupplier({
            id: packageId,
            suppliers: suppliersToAdd,
          }),
        );
      }

      if (categoriesToAdd.length > 0) {
        additionRequests.push(
          this.packageRepo.addPackageCategory({
            id: packageId,
            categories: categoriesToAdd,
          }),
        );
      }

      const additionResults = await Promise.allSettled(additionRequests);
      const failedAddition = additionResults.find(
        (result) => result.status === "rejected",
      );

      if (failedAddition?.status === "rejected") {
        throw failedAddition.reason;
      }

      return response;
    }

    return this.packageRepo.createPackage(
      this.buildCreateRequest(command.service as MobileDataService, request),
    );
  }

  async getPackageCategories(
    params: PackageLookupRequestEntity,
  ): Promise<PackageCategoryEntity[]> {
    const categories = await this.filterUseCase.fetchListWithFeature<
      PackageLookupRequestEntity,
      PackageCategoryEntity
    >({
      path: CATEGORY_MANAGEMENT.LIST,
      params,
      savedKey: CacheKeyEnum.categoryListForPackage,
      isForceRefresh: false,
      isNeedFeature: false,
    });

    return (categories ?? []).filter(
      (category) =>
        category.code?.includes("MD_") ||
        category.code === "SP_travel" ||
        category.code === "SP_ecommerce",
    );
  }

  async getPackageCountries(
    params: PackageLookupRequestEntity,
  ): Promise<PackageLookupEntity[]> {
    return (
      (await this.filterUseCase.fetchListWithFeature({
        path: FILTER_FEATURE.NATION_LIST,
        params,
        isNeedFeature: false,
      })) ?? []
    );
  }

  async getPackageSuppliers(
    params: PackageLookupRequestEntity,
  ): Promise<PackageSupplierEntity[]> {
    return (
      (await this.filterUseCase.fetchListWithFeature({
        path: SUPPLIER_MANAGEMENT.LIST,
        params,
        isNeedFeature: false,
      })) ?? []
    );
  }

  async getPackageRoamingPartners(
    params: PackageLookupRequestEntity,
  ): Promise<PackageLookupEntity[]> {
    return (
      (await this.filterUseCase.fetchListWithFeature({
        path: ROAMING_PARTNER.LIST,
        params,
        isNeedFeature: false,
      })) ?? []
    );
  }

  async getPackageCarriers(
    params: PackageLookupRequestEntity,
  ): Promise<PackageLookupEntity[]> {
    const carriers =
      (await this.filterUseCase.fetchListWithFeature<
        PackageLookupRequestEntity,
        PackageLookupEntity
      >({
        path: CARRIER_MANAGEMENT.LIST,
        params,
        savedKey: CacheKeyEnum.carrierForPackage,
        isForceRefresh: false,
      })) ?? [];

    return carriers
      .filter((carrier) =>
        ["viettel", "mobifone", "vinaphone"].includes(carrier.code ?? ""),
      )
      .map((carrier) => ({
        ...carrier,
        id: carrier.id ?? carrierIds[carrier.code ?? ""],
      }));
  }
}
